/**
 * Key levels drawn as right-axis tags: GEX (walls, zero gamma, IV bands),
 * volume profile (session + weekly POC/VAH/VAL) and the overnight high/low.
 *
 * Pure functions so the chart effect, the hover tooltip and the confluence
 * check all agree on what each level is, what it is called and what it means.
 * Every GEX price arriving here is already in futures points (the engine
 * converted it at the chain's own quote time) - never rescale.
 */
import type { GexLevels, GexElement } from '../store/gexStore';
import type { ProfileLevels } from '../store/profileLevelsStore';
import type { OnhOnl } from './onhOnl';

export type KeyLevelKind =
  | 'poc' | 'vah' | 'val'
  | 'wpoc' | 'wvah' | 'wval'
  | 'zeroGamma' | 'putWall' | 'callWall' | 'ivUp' | 'ivDown'
  | 'pdh' | 'pdl' | 'moc'
  | 'onh' | 'onl';

export interface KeyLevel {
  kind: KeyLevelKind;
  label: string;
  price: number;
  color: string;
  /** lightweight-charts LineStyle: 0 solid, 1 dotted, 2 dashed, 3 large dashed. */
  lineStyle: 0 | 1 | 2 | 3;
  /** Still forming (ONH/ONL before 08:30 CT): drawn lighter, can still move. */
  developing?: boolean;
}

/** Tag text, upper case like the reference chart's axis tags. */
export const KEY_LEVEL_LABELS: Record<KeyLevelKind, string> = {
  poc: 'POC',
  vah: 'VAH',
  val: 'VAL',
  wpoc: 'wPOC',
  wvah: 'wVAH',
  wval: 'wVAL',
  zeroGamma: 'ZERO GAMMA',
  putWall: 'PUT WALL',
  callWall: 'CALL WALL',
  ivUp: 'IV+',
  ivDown: 'IV-',
  pdh: 'PDH',
  pdl: 'PDL',
  moc: 'MOC',
  onh: 'ONH',
  onl: 'ONL',
};

/**
 * Colours follow the GEX convention traders already read at a glance (put wall
 * green = support, call wall red = resistance, zero gamma amber) and the
 * volume-profile primitive's own colours (POC gold, value area cyan).
 */
export const KEY_LEVEL_COLORS: Record<KeyLevelKind, string> = {
  poc: '#FFD700',
  vah: '#00BCD4',
  val: '#00BCD4',
  wpoc: '#d4a017',
  wvah: '#7e57c2',
  wval: '#7e57c2',
  zeroGamma: '#ffa726',
  putWall: '#26a69a',
  callWall: '#ef5350',
  ivUp: '#64b5f6',
  ivDown: '#64b5f6',
  pdh: '#8b5cf6',
  pdl: '#ec4899',
  moc: '#ff5a1f',
  onh: '#a3e635',
  onl: '#fb7185',
};

const LINE_STYLE: Record<KeyLevelKind, KeyLevel['lineStyle']> = {
  poc: 0, vah: 2, val: 2,
  wpoc: 0, wvah: 3, wval: 3,
  zeroGamma: 0, putWall: 0, callWall: 0, ivUp: 1, ivDown: 1,
  pdh: 2, pdl: 2, moc: 0,
  onh: 0, onl: 0,
};

/**
 * One plain line per level, shown on hover/tap. `vaPct` fills in the user's
 * value-area percentage so the VAH/VAL text matches what was computed.
 */
export function keyLevelExplanation(kind: KeyLevelKind, vaPct = 70): string {
  const va = `${Math.round(vaPct)}%`;
  switch (kind) {
    case 'putWall':
      return 'Biggest put-gamma strike. Dealers tend to buy into it, so it often acts as support and absorbs selling; a clean break can accelerate.';
    case 'callWall':
      return 'Biggest call-gamma strike. Tends to cap rallies.';
    case 'zeroGamma':
      return 'Dealer positioning flips here. Above it moves tend to mean-revert; below it they tend to trend and expand.';
    case 'ivUp':
      return 'Top of the options-implied expected range (anchor, IV and horizon in the line below).';
    case 'ivDown':
      return 'Bottom of the options-implied expected range (anchor, IV and horizon in the line below).';
    case 'poc':
      return 'Point of control: the most-traded price this session.';
    case 'vah':
      return `Value area high: top edge of the value area (${va} of this session's volume).`;
    case 'val':
      return `Value area low: bottom edge of the value area (${va} of this session's volume).`;
    case 'wpoc':
      return 'Weekly point of control: the most-traded price this CME week.';
    case 'wvah':
      return `Weekly value area high: top edge of the value area (${va} of this week's volume).`;
    case 'wval':
      return `Weekly value area low: bottom edge of the value area (${va} of this week's volume).`;
    case 'pdh':
      return 'Previous day high.';
    case 'pdl':
      return 'Previous day low.';
    case 'moc':
      return 'MOC imbalance level: open of the 2:50 PM CT candle.';
    case 'onh':
    case 'onl':
      return ONH_ONL_EXPLANATION;
  }
}

export const ONH_ONL_EXPLANATION =
  'Overnight high/low (5 PM - 8:30 AM CT). Often tested early in the cash session; acceptance above/below tends to extend.';
export const ONH_ONL_DEVELOPING_NOTE = 'Developing: the overnight is still running, fixes at 8:30 AM CT.';

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && v > 0;

function level(kind: KeyLevelKind, price: number): KeyLevel {
  return { kind, label: KEY_LEVEL_LABELS[kind], price, color: KEY_LEVEL_COLORS[kind], lineStyle: LINE_STYLE[kind] };
}

/**
 * GEX levels to draw. The engine already applied the expiry window and method
 * chosen in its settings to the top-level fields, so they are drawn as they
 * are - no client-side scope switching.
 */
export function gexKeyLevels(
  gex: GexLevels | null,
  visibility: Record<GexElement, boolean>,
): KeyLevel[] {
  if (!gex) return [];
  const out: KeyLevel[] = [];
  if (visibility.callWall && isNum(gex.callWall)) out.push(level('callWall', gex.callWall));
  if (visibility.gammaFlip && isNum(gex.gammaFlip)) out.push(level('zeroGamma', gex.gammaFlip));
  if (visibility.putWall && isNum(gex.putWall)) out.push(level('putWall', gex.putWall));
  if (visibility.ivBands) {
    if (isNum(gex.ivUpper)) out.push(level('ivUp', gex.ivUpper));
    if (isNum(gex.ivLower)) out.push(level('ivDown', gex.ivLower));
  }
  return out;
}

export interface ProfileVisibility {
  sessionProfile: boolean;
  weeklyProfile: boolean;
}

/** Session and weekly POC/VAH/VAL. */
export function profileKeyLevels(p: ProfileLevels | null | undefined, vis: ProfileVisibility): KeyLevel[] {
  if (!p) return [];
  const out: KeyLevel[] = [];
  if (vis.sessionProfile && p.session) {
    if (isNum(p.session.vah)) out.push(level('vah', p.session.vah));
    if (isNum(p.session.poc)) out.push(level('poc', p.session.poc));
    if (isNum(p.session.val)) out.push(level('val', p.session.val));
  }
  if (vis.weeklyProfile && p.weekly) {
    if (isNum(p.weekly.vah)) out.push(level('wvah', p.weekly.vah));
    if (isNum(p.weekly.poc)) out.push(level('wpoc', p.weekly.poc));
    if (isNum(p.weekly.val)) out.push(level('wval', p.weekly.val));
  }
  return out;
}

/**
 * `#rrggbb` at reduced opacity (`#rrggbbaa`). Used for developing levels so
 * they read as provisional next to fixed ones. Anything that is not a 6-digit
 * hex colour is returned unchanged rather than mangled.
 */
export function lighten(color: string, alpha = 0.55): string {
  if (!/^#[0-9a-fA-F]{6}$/.test(color)) return color;
  const a = Math.round(Math.max(0, Math.min(1, alpha)) * 255).toString(16).padStart(2, '0');
  return `${color}${a}`;
}

/**
 * ONH/ONL tags. Fixed (from 08:30 CT): solid in the configured colours.
 * Developing (overnight still running): dotted and translucent.
 */
export function onhOnlKeyLevels(
  on: OnhOnl | null | undefined,
  colors: { high: string; low: string } = { high: KEY_LEVEL_COLORS.onh, low: KEY_LEVEL_COLORS.onl },
): KeyLevel[] {
  if (!on) return [];
  const out: KeyLevel[] = [];
  const make = (kind: 'onh' | 'onl', price: number, color: string): KeyLevel => ({
    kind,
    label: KEY_LEVEL_LABELS[kind],
    price,
    color: on.developing ? lighten(color) : color,
    lineStyle: on.developing ? 1 : LINE_STYLE[kind],
    developing: on.developing,
  });
  if (isNum(on.high)) out.push(make('onh', on.high, colors.high));
  if (isNum(on.low)) out.push(make('onl', on.low, colors.low));
  return out;
}

/** PDH/PDL/MOC so they can take part in confluence (they draw elsewhere). */
export function sessionKeyLevels(input: { pdh?: number; pdl?: number; moc?: number }): KeyLevel[] {
  const out: KeyLevel[] = [];
  if (isNum(input.pdh)) out.push(level('pdh', input.pdh));
  if (isNum(input.pdl)) out.push(level('pdl', input.pdl));
  if (isNum(input.moc)) out.push(level('moc', input.moc));
  return out;
}

const GEX_KINDS: ReadonlySet<KeyLevelKind> = new Set(['zeroGamma', 'putWall', 'callWall', 'ivUp', 'ivDown']);
export const isGexKind = (k: KeyLevelKind) => GEX_KINDS.has(k);

const etTime = (ms: number) =>
  new Date(ms).toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', hour12: false });
const etDay = (ms: number) =>
  new Date(ms).toLocaleDateString('en-US', { timeZone: 'America/New_York', weekday: 'short', month: 'short', day: 'numeric' });

/**
 * Where the GEX numbers come from and how old they are, e.g.
 * "CBOE chain as of 11:15 ET, 15 min delayed". Outside the cash session the
 * chain's spot is the 16:00 close, so it says that instead.
 */
export function gexSourceLine(
  gex: Pick<GexLevels, 'quoteTime' | 'chainTime'> | null,
  now: number = Date.now(),
): string {
  if (!gex) return 'CBOE chain unavailable';
  const q = gex.quoteTime;
  if (!q) return 'CBOE chain (quote time unknown), 15 min delayed';
  const t = etTime(q);
  const base = t === '16:00' || t === '15:59'
    ? `CBOE chain as of the 16:00 ET close (${etDay(q)})`
    : `CBOE chain as of ${t} ET, 15 min delayed`;
  // CBOE publishes around the clock (every ~15 min), so a chain hours old means
  // the writer engine has stopped fetching, not that the market is shut.
  const ageH = gex.chainTime ? (now - gex.chainTime) / 3_600_000 : 0;
  return ageH > 2 ? `${base} · STALE: no new chain for ${Math.floor(ageH)}h` : base;
}

/**
 * Which expiry window and method produced a GEX level, for its tooltip, e.g.
 * "0-2 DTE (261009, 261012) · net GEX by strike, OI + volume" (the put wall
 * names its own method, "OI only" by default). Null for
 * non-GEX kinds and for engines that do not report a selection.
 */
export function gexMethodLine(kind: KeyLevelKind, gex: Pick<GexLevels, 'selection'> | null | undefined): string | null {
  const sel = gex?.selection;
  if (!sel || !isGexKind(kind)) return null;
  let parts: string[];
  if (kind === 'ivUp' || kind === 'ivDown') {
    parts = [sel.ivBands];
  } else {
    // The put wall has its own method (OI only by default) and, when that
    // book selected different expiries, its own expiry list.
    const isPut = kind === 'putWall';
    const how = kind === 'zeroGamma' ? sel.zeroGamma : isPut ? sel.putWall || sel.walls : sel.walls;
    const label = sel.expiryWindowLabel || sel.expiryWindow;
    const exps = isPut && sel.putWallExpiries && sel.putWallExpiries.length > 0 ? sel.putWallExpiries : sel.expiries;
    const window = label && exps && exps.length > 0 ? `${label} (${exps.join(', ')})` : label;
    parts = [window, how];
  }
  parts = parts.filter((p): p is string => typeof p === 'string' && p.length > 0);
  // Say so when the pre-calibration method drew it, unless the engine's own
  // wording already does.
  if (sel.method === 'legacy' && !parts.some((p) => /legacy/i.test(p))) parts.push('legacy');
  return parts.length > 0 ? parts.join(' · ') : null;
}

/** Nearest item whose y is within `tol` CSS px of `y`. */
export function hitTestY<T extends { y: number }>(items: T[], y: number, tol = 4): T | null {
  let best: T | null = null;
  let bestD = tol + 1e-9;
  for (const it of items) {
    const d = Math.abs(it.y - y);
    if (d <= bestD) {
      best = it;
      bestD = d;
    }
  }
  return best;
}
